import {Box, Flex, Icon, IconWithText, Loader, PositionedFlex, Text, Theme} from 'exsportia-components'
import {createContext, FC, PropsWithChildren, useCallback, useContext, useEffect, useState} from 'react'
import {useMutation} from 'react-query'

import {PaymentsMutations} from '../../../api/payments'
import {useConfirmationModal} from '../../../components/confirmation-modal/confirmation-modal'
import {ThemeType} from '../../../theme/types'
import {Button} from '../../../ui/components/button/button'
import {useLocale, useLuxon} from '../../../utils/i18n'
import {useBookingLoader} from '../../booking/hooks'
import {openPaymentFormType, useTriggerProviderPayment} from './trigger-provider-payment.hook'

const theme = Theme as ThemeType

const iframeWhapSettings = {
  top: 0,
  left: 0,
  zIndex: 4,
  width: '100vw',
  height: '100vh',
  overflow: 'auto',
  position: 'fixed',
  bg: theme.colors.bg,
}

const iframeSettings = {
  width: '100%',
  border: 'none',
  height: '100%',
}

type setIframeSrcType = (url: string | null) => void
type PaymentProviderContextType = {
  clearUrls?: () => void
  clearWithConfirm?: () => void
  imageSrc: string | null
  iframeSrc: string | null
  setIframeSrc: setIframeSrcType | null
  openPaymentForm: openPaymentFormType | null
  handleLinkCallback: ((args: handleLinkCallbackArgsType) => void) | null
}

const PaymentProviderContext = createContext<PaymentProviderContextType>({
  imageSrc: null,
  iframeSrc: null,
  setIframeSrc: null,
  openPaymentForm: null,
  handleLinkCallback: null,
})

export const usePaymentProvider = () => useContext(PaymentProviderContext)

export type handleLinkCallbackArgsType = {
  url?: string | null
  type?: string | null
  expires_at?: number | null
  downloadUrl?: string | null
}

export const PaymentProviderProvider: FC<PropsWithChildren> = ({children}) => {
  const luxon = useLuxon()
  const {l} = useLocale()
  const [imageLoading, setImageLoading] = useState(true)
  const [iframeSrc, setIframeSrc] = useState<string | null>(null)
  const [imageSrc, setImageSrc] = useState<string | null>(null)
  const [expiresAt, setExpiresAt] = useState<number | null>(null)
  const [currentEntity, setCurrentEntity] = useState<string | null>(null)
  const [currentEntityId, setCurrentEntityId] = useState<string | null>(null)
  const {setIsLoading} = useBookingLoader()
  const {mutate: updateEntityForPay} = useMutation(PaymentsMutations.UpdateEntityForPayMutation({}))
  const {openConfirmationModal} = useConfirmationModal()

  const handleLinkCallback = ({
    downloadUrl = null,
    url = null,
    type = null,
    expires_at = null,
  }: handleLinkCallbackArgsType) => {
    if (expires_at) {
      setExpiresAt(expires_at)
    }
    if (!downloadUrl) {
      setImageSrc(null)
    }
    if (!url) {
      setIframeSrc(null)
    }
    if (type === 'image' && downloadUrl) {
      setImageLoading(true)
      setImageSrc(downloadUrl)
      return
      // return setDownloadSrc(downloadUrl)
    }
    if (url) {
      window.open(url, '_self')
    }
  }

  const {openPaymentForm: innerOpenPaymentForm} = useTriggerProviderPayment({handleLinkCallback})

  const openPaymentForm: openPaymentFormType = (params) => {
    setCurrentEntity(params.entity || null)
    setCurrentEntityId(params.bookingId || null)
    return innerOpenPaymentForm(params)
  }
  const clearUrls = useCallback(() => {
    setIsLoading(false)
    setIframeSrc(null)
    setImageSrc(null)
    setExpiresAt(null)
    setCurrentEntity(null)
    setCurrentEntityId(null)
  }, [setIsLoading])

  const clearUrlsHandler = useCallback(() => {
    const isPaymentInProcess = Boolean(iframeSrc || imageSrc)
    if (isPaymentInProcess) {
      openConfirmationModal({
        description: 'areYouSureCancelPayment',
        onConfirm: () => {
          clearUrls()
          if (currentEntity && currentEntityId) {
            updateEntityForPay(
              {
                entity: currentEntity,
                entityId: currentEntityId,
                status: 'PAYMENT_FAIL',
                subStatus: null,
                expires_at: null,
                occupySlot: false,
              },
              {
              }
            )
          }
        },
      })
      return
    }
    clearUrls()
  }, [iframeSrc, imageSrc, currentEntity, currentEntityId, updateEntityForPay, clearUrls, openConfirmationModal])

  useEffect(() => {
    const isPaymentInProcess = Boolean(iframeSrc || imageSrc)
    if (isPaymentInProcess) {
      const handler = (e: BeforeUnloadEvent) => {
        e.preventDefault()
        e.returnValue = ''
      }
      window.addEventListener('beforeunload', handler)
      return () => {
        window.removeEventListener('beforeunload', handler)
      }
    }
    return undefined
  }, [iframeSrc, imageSrc])


  return (
    <PaymentProviderContext.Provider
      value={{
        iframeSrc,
        imageSrc,
        clearUrls,
        clearWithConfirm: clearUrlsHandler,
        setIframeSrc,
        openPaymentForm,
        handleLinkCallback,
      }}
    >
      <div>
        {children}
        {(iframeSrc || imageSrc) && (
          <Box {...iframeWhapSettings}>
            {iframeSrc && (
              <iframe
                src={iframeSrc}
                style={iframeSettings}
                title='Payment Authorization'
              />
            )}
            {imageSrc && (
              <Flex
                width='100%'
                justifyContent='center'
                alignItems='flex-start'
              >
                <Flex
                  gap='8px'
                  alignItems='center'
                  flexDirection='column'
                  maxWidth='95vw'
                  width='500px'
                >
                  <img
                    src={imageSrc}
                    alt='Downloaded'
                    style={{maxWidth: '100%', display: imageLoading ? 'none' : 'block'}}
                    onLoad={() => setImageLoading(false)}
                  />
                  {imageLoading && <Loader />}
                  <Text settings={{textAlign: 'center'}}>{l('qr-pay-mobile-users-instructions')}</Text>
                  <Text settings={{textAlign: 'center'}}>{l('qr-pay-pc-users-instructions')}</Text>
                  {expiresAt && (
                    <IconWithText
                      settings={{justifyContent: 'center'}}
                      text={`Expire at ${luxon.fromMillis(expiresAt).toFormat('HH:mm')}`}
                      icon='clock'
                    />
                  )}
                </Flex>
              </Flex>
            )}
            <PositionedFlex
              top='8px'
              left='8px'
              width='40px'
              height='40px'
              alignItems='center'
              position='absolute'
              justifyContent='center'
            >
              <Button
                type='flat_short'
                onClick={clearUrlsHandler}
              >
                <Icon icon='arrowLeft' />
              </Button>
            </PositionedFlex>
          </Box>
        )}
      </div>
    </PaymentProviderContext.Provider>
  )
}
